import { Href } from 'redux-little-router';

import { MetroStationInfo, VcardData } from '../typings/banners';
import {
    DataForEditCampaigns,
    EditCampaignValueField,
    FetchCampaignsGoalsAvailableForKeyGoalsInput
} from '../typings/campaignsEdit';
import { ChatListValue } from '../typings/chatList';
import { PageStatus, ValidationErrorData } from '../typings/common';
import { CampaignId } from '../typings/domain';
import { ExperimentsSaveValue } from '../typings/experimentsEditor';
import { Goal } from '../typings/goals';
import { GdContentLanguage } from '../typings/graphql-grid';
import { MetrikaCounterEditorValue } from '../typings/metrikaCounterEditor';
import { MetrikaSelectedKeyGoal } from '../typings/metrikaKeyGoals';
import { MinusPhrasesValue } from '../typings/minusPhrases';
import { NotificationsValue } from '../typings/notificationEditor';
import {
    RateCorrection,
    RateCorrectionType,
    RetargetingConditionDataForRateCorrections
} from '../typings/ratesCorrections';
import { RelevantPhrasesValue } from '../typings/relevantPhrases';
import { Strategy } from '../typings/strategy';
import { TimeTargetingValue } from '../typings/timeTargetingEditor';

export const UPDATE_DATA_FOR_EDIT_CAMPAIGNS = 'UPDATE_DATA_FOR_EDIT_CAMPAIGNS';
export type UPDATE_DATA_FOR_EDIT_CAMPAIGNS = typeof UPDATE_DATA_FOR_EDIT_CAMPAIGNS;

export interface UpdateDataForEditCampaignsAction {
    type: UPDATE_DATA_FOR_EDIT_CAMPAIGNS;
    data: DataForEditCampaigns;
}

export function updateDataForEditCampaigns(
    data: DataForEditCampaigns
): UpdateDataForEditCampaignsAction {
    return {
        type: UPDATE_DATA_FOR_EDIT_CAMPAIGNS,
        data: data
    };
}

export const GET_CAMPAIGNS_FOR_EDIT = 'GET_CAMPAIGNS_FOR_EDIT';
export type GET_CAMPAIGNS_FOR_EDIT = typeof GET_CAMPAIGNS_FOR_EDIT;

export interface GetCampaignsForEditAction {
    type: GET_CAMPAIGNS_FOR_EDIT;
}

export function getCampaignsForEdit(): GetCampaignsForEditAction {
    return {
        type: GET_CAMPAIGNS_FOR_EDIT
    };
}

export const GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT =
    'GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT';
export type GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT = typeof GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT;

export interface GetRetargetingConditionsForCampaignsEditAction {
    type: GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT;
    login: string;
}

export function getRetargetingConditionsForCampaignsEdit(
    login: string
): GetRetargetingConditionsForCampaignsEditAction {
    return {
        type: GET_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT,
        login
    };
}

export const UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT =
    'UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT';
export type UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT = typeof UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT;

export interface UpdateRetargetingConditionsForCampaignsEditAction {
    type: UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT;
    retargetingConditions: RetargetingConditionDataForRateCorrections[];
}

export function updateRetargetingConditionsForCampaignsEdit(
    retargetingConditions: RetargetingConditionDataForRateCorrections[]
): UpdateRetargetingConditionsForCampaignsEditAction {
    return {
        type: UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT,
        retargetingConditions
    };
}

export const FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS =
    'FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS';
export type FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS = typeof FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS;

export interface FetchCampaignsGoalsAvailableForKeyGoals {
    type: FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS;
    fetchAvailableForKeyGoalsInput: FetchCampaignsGoalsAvailableForKeyGoalsInput[];
}

export function fetchCampaignsGoalsAvailableForKeyGoals(
    fetchAvailableForKeyGoalsInput: FetchCampaignsGoalsAvailableForKeyGoalsInput[]
): FetchCampaignsGoalsAvailableForKeyGoals {
    return {
        type: FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS,
        fetchAvailableForKeyGoalsInput
    };
}

export const SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR =
    'SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR';
export type SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR = typeof SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR;

export interface ShowCampaignEditGlobalErrorAction {
    type: SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR;
    error: {
        title: string;
        hint: string;
    };
}

export function showCampaignEditGlobalError(error: {
    title: string;
    hint: string;
}): ShowCampaignEditGlobalErrorAction {
    return {
        type: SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR,
        error
    };
}

export const SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS =
    'SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS';
export type SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS = typeof SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS;

export interface ShowCampaignEditGeneralErrorsAction {
    type: SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS;
    errors: ValidationErrorData[];
}

export function showCampaignEditGeneralErrors(
    errors: ValidationErrorData[]
): ShowCampaignEditGeneralErrorsAction {
    return {
        type: SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS,
        errors
    };
}

export const SHOW_CAMPAIGN_EDIT_ITEM_ERRORS = 'SHOW_CAMPAIGN_EDIT_ITEM_ERRORS';
export type SHOW_CAMPAIGN_EDIT_ITEM_ERRORS = typeof SHOW_CAMPAIGN_EDIT_ITEM_ERRORS;

export interface ShowCampaignEditItemErrorsAction {
    type: SHOW_CAMPAIGN_EDIT_ITEM_ERRORS;
    campaignId: string;
    errorKey: 'general' | EditCampaignValueField;
    errors: ValidationErrorData[];
}

export function showCampaignEditItemErrors(
    campaignId: string,
    errorKey: 'general' | EditCampaignValueField,
    errors: ValidationErrorData[]
): ShowCampaignEditItemErrorsAction {
    return {
        type: SHOW_CAMPAIGN_EDIT_ITEM_ERRORS,
        errorKey,
        campaignId,
        errors
    };
}

export const SAVE_CAMPAIGNS = 'SAVE_CAMPAIGNS';
export type SAVE_CAMPAIGNS = typeof SAVE_CAMPAIGNS;

export interface SaveCampaignsAction {
    type: SAVE_CAMPAIGNS;
}

export function saveCampaigns(): SaveCampaignsAction {
    return {
        type: SAVE_CAMPAIGNS
    };
}

export const LEAVE_CAMPAIGNS_EDIT = 'LEAVE_CAMPAIGNS_EDIT';
export type LEAVE_CAMPAIGNS_EDIT = typeof LEAVE_CAMPAIGNS_EDIT;

export interface LeaveCampaignsEditAction {
    type: LEAVE_CAMPAIGNS_EDIT;
    location: Href;
    showConfirm?: boolean;
}

export function leaveCampaignsEdit(
    location: Href,
    showConfirm: boolean
): LeaveCampaignsEditAction {
    return {
        type: LEAVE_CAMPAIGNS_EDIT,
        location,
        showConfirm
    };
}

export const CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS =
    'CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS';
export type CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS = typeof CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS;

export interface ChangeCampaignsEditPageStatusAction {
    type: CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS;
    status: PageStatus;
}

export function changeCampaignsEditPageStatus(
    status: PageStatus
): ChangeCampaignsEditPageStatusAction {
    return {
        type: CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS,
        status
    };
}

export const UPDATE_CAMPAIGN_CHAT = 'UPDATE_CAMPAIGN_CHAT';
export type UPDATE_CAMPAIGN_CHAT = typeof UPDATE_CAMPAIGN_CHAT;

export interface UpdateCampaignChat {
    type: UPDATE_CAMPAIGN_CHAT;
    campaignId: string;
    chatValue: ChatListValue;
}

export function updateCampaignChat(
    campaignId: string,
    chatValue: ChatListValue
): UpdateCampaignChat {
    return {
        type: UPDATE_CAMPAIGN_CHAT,
        campaignId,
        chatValue
    };
}

export const UPDATE_CAMPAIGN_RELEVANT_PHRASES =
    'UPDATE_CAMPAIGN_RELEVANT_PHRASES';
export type UPDATE_CAMPAIGN_RELEVANT_PHRASES = typeof UPDATE_CAMPAIGN_RELEVANT_PHRASES;

export interface UpdateCampaignRelevantPhrases {
    type: UPDATE_CAMPAIGN_RELEVANT_PHRASES;
    campaignId: string;
    relevantPhrases: RelevantPhrasesValue;
}

export function updateCampaignRelevantPhrases(
    campaignId: string,
    relevantPhrases: RelevantPhrasesValue
): UpdateCampaignRelevantPhrases {
    return {
        type: UPDATE_CAMPAIGN_RELEVANT_PHRASES,
        campaignId,
        relevantPhrases
    };
}

export const SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR =
    'SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR';
export type SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR = typeof SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR;

export interface SaveCampaignMetrikaCounterEditorAction {
    type: SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR;
    campaignId: string;
}

export function saveCampaignMetrikaCounterEditor(
    campaignId: string
): SaveCampaignMetrikaCounterEditorAction {
    return {
        type: SAVE_CAMPAIGN_METRIKA_COUNTER_EDITOR,
        campaignId
    };
}

export const UPDATE_CAMPAIGN_METRIKA_COUNTERS =
    'UPDATE_CAMPAIGN_METRIKA_COUNTERS';
export type UPDATE_CAMPAIGN_METRIKA_COUNTERS = typeof UPDATE_CAMPAIGN_METRIKA_COUNTERS;

export interface UpdateCampaignMetrikaCounters {
    type: UPDATE_CAMPAIGN_METRIKA_COUNTERS;
    campaignId: string;
    metrikaCounters: MetrikaCounterEditorValue;
}

export function updateCampaignMetrikaCounters(
    campaignId: string,
    metrikaCounters: MetrikaCounterEditorValue
): UpdateCampaignMetrikaCounters {
    return {
        type: UPDATE_CAMPAIGN_METRIKA_COUNTERS,
        campaignId,
        metrikaCounters
    };
}

export const SAVE_CAMPAIGN_NAME_EDITOR_CHANGES =
    'SAVE_CAMPAIGN_NAME_EDITOR_CHANGES';
export type SAVE_CAMPAIGN_NAME_EDITOR_CHANGES = typeof SAVE_CAMPAIGN_NAME_EDITOR_CHANGES;

export interface SaveCampaignNameEditorChangesAction {
    type: SAVE_CAMPAIGN_NAME_EDITOR_CHANGES;
    campaignId: string;
}

export function saveCampaignNameEditorChanges(
    campaignId: string
): SaveCampaignNameEditorChangesAction {
    return {
        type: SAVE_CAMPAIGN_NAME_EDITOR_CHANGES,
        campaignId
    };
}

export const UPDATE_CAMPAIGN_NAME = 'UPDATE_CAMPAIGN_NAME';
export type UPDATE_CAMPAIGN_NAME = typeof UPDATE_CAMPAIGN_NAME;

export interface UpdateCampaignNameAction {
    type: UPDATE_CAMPAIGN_NAME;
    campaignId: string;
    name: string;
}

export function updateCampaignName(
    campaignId: string,
    name: string
): UpdateCampaignNameAction {
    return {
        type: UPDATE_CAMPAIGN_NAME,
        campaignId,
        name
    };
}

export const CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG =
    'CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG';
export type CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG = typeof CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG;

export interface ChangeBannersTextSubstitutionAction {
    type: CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG;
    campaignId: string;
}

export function changeBannersTextSubstitution(
    campaignId: string
): ChangeBannersTextSubstitutionAction {
    return {
        type: CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG,
        campaignId
    };
}

export const CHANGE_HAS_EXTENDED_GEO_TARGETING =
    'CHANGE_HAS_EXTENDED_GEO_TARGETING';
export type CHANGE_HAS_EXTENDED_GEO_TARGETING = typeof CHANGE_HAS_EXTENDED_GEO_TARGETING;

export interface ChangeHasExtendedGeoTargetingAction {
    type: CHANGE_HAS_EXTENDED_GEO_TARGETING;
    campaignId: string;
}

export function changeHasExtendedGeoTargeting(
    campaignId: string
): ChangeHasExtendedGeoTargetingAction {
    return {
        type: CHANGE_HAS_EXTENDED_GEO_TARGETING,
        campaignId
    };
}

export const CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS =
    'CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS';
export type CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS = typeof CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS;

export interface ChangeSiteMonitoringFlagForEditCampaigns {
    type: CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
}

export function changeSiteMonitoringFlagForEditCampaigns(
    campaignId: string
): ChangeSiteMonitoringFlagForEditCampaigns {
    return {
        type: CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS,
        campaignId
    };
}

export const CHANGE_MARK_TARGET_CALL_LINKS_FLAG =
    'CHANGE_MARK_TARGET_CALL_LINKS_FLAG';
export type CHANGE_MARK_TARGET_CALL_LINKS_FLAG = typeof CHANGE_MARK_TARGET_CALL_LINKS_FLAG;

export interface ChangeMarkTargetCallLinksFlagAction {
    type: CHANGE_MARK_TARGET_CALL_LINKS_FLAG;
    campaignId: string;
}

export function changeMarkTargetCallLinksFlag(
    campaignId: string
): ChangeMarkTargetCallLinksFlagAction {
    return {
        type: CHANGE_MARK_TARGET_CALL_LINKS_FLAG,
        campaignId
    };
}

export const CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG =
    'CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG';
export type CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG = typeof CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG;

export interface ChangeShowYandexCatalogInfoAction {
    type: CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG;
    campaignId: string;
}

export function changeShowYandexCatalogInfo(
    campaignId: string
): ChangeShowYandexCatalogInfoAction {
    return {
        type: CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG,
        campaignId
    };
}

export const CHANGE_CONSIDER_COMPETITORS_FLAG =
    'CHANGE_CONSIDER_COMPETITORS_FLAG';
export type CHANGE_CONSIDER_COMPETITORS_FLAG = typeof CHANGE_CONSIDER_COMPETITORS_FLAG;

export interface ChangeConsiderCompetitorsAction {
    type: CHANGE_CONSIDER_COMPETITORS_FLAG;
    campaignId: string;
}

export function changeConsiderCompetitors(
    campaignId: string
): ChangeConsiderCompetitorsAction {
    return {
        type: CHANGE_CONSIDER_COMPETITORS_FLAG,
        campaignId
    };
}

export const TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY =
    'TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY';
export type TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY = typeof TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY;

export interface ToggleCampaignOtherSettingsVisibilityAction {
    type: TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY;
    campaignId: string;
}

export function toggleCampaignOtherSettingsVisibilityAction(
    campaignId: string
): ToggleCampaignOtherSettingsVisibilityAction {
    return {
        type: TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY,
        campaignId
    };
}

export const TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY =
    'TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY';
export type TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY = typeof TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY;

export interface ToggleCampaignsOtherSettingsVisibilityAction {
    type: TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY;
    campaignIds: string[];
}

export function toggleCampaignsOtherSettingsVisibilityAction(
    campaignIds: string[]
): ToggleCampaignsOtherSettingsVisibilityAction {
    return {
        type: TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY,
        campaignIds
    };
}

export const CHANGE_CAMPAIGN_BANNER_LANGUAGE =
    'CHANGE_CAMPAIGN_BANNER_LANGUAGE';
export type CHANGE_CAMPAIGN_BANNER_LANGUAGE = typeof CHANGE_CAMPAIGN_BANNER_LANGUAGE;

export interface ChangeBannersLanguage {
    type: CHANGE_CAMPAIGN_BANNER_LANGUAGE;
    campaignId: string;
    languageCode?: GdContentLanguage;
}

export function changeBannersLanguage(
    campaignId: string,
    languageCode?: GdContentLanguage
): ChangeBannersLanguage {
    return {
        type: CHANGE_CAMPAIGN_BANNER_LANGUAGE,
        campaignId,
        languageCode
    };
}

export const ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR =
    'ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR';
export type ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR = typeof ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR;

export interface OnDoneCampaignDisabledIpsEditor {
    type: ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR;
    campaignId: string;
    value: string[];
}

export function onDoneCampaignDisabledIpsEditor(
    campaignId: string,
    value: string[]
): OnDoneCampaignDisabledIpsEditor {
    return {
        type: ON_DONE_CAMPAIGN_DISABLED_IPS_EDITOR,
        campaignId,
        value
    };
}

export const UPDATE_CAMPAIGN_DISABLED_IPS_VALUE =
    'UPDATE_CAMPAIGN_DISABLED_IPS_VALUE';
export type UPDATE_CAMPAIGN_DISABLED_IPS_VALUE = typeof UPDATE_CAMPAIGN_DISABLED_IPS_VALUE;

export interface UpdateCampaignDisabledIpsValueAction {
    type: UPDATE_CAMPAIGN_DISABLED_IPS_VALUE;
    campaignId: string;
    value: string[];
}

export function updateCampaignDisabledIpsValue(
    campaignId: string,
    value: string[]
): UpdateCampaignDisabledIpsValueAction {
    return {
        type: UPDATE_CAMPAIGN_DISABLED_IPS_VALUE,
        campaignId,
        value
    };
}

export const SAVE_CAMPAIGN_CHAT_LIST_EDITOR = 'SAVE_CAMPAIGN_CHAT_LIST_EDITOR';
export type SAVE_CAMPAIGN_CHAT_LIST_EDITOR = typeof SAVE_CAMPAIGN_CHAT_LIST_EDITOR;

export interface SaveCampaignChatListEditorAction {
    type: SAVE_CAMPAIGN_CHAT_LIST_EDITOR;
    campaignId: string;
}

export function saveCampaignChatListEditor(
    campaignId: string
): SaveCampaignChatListEditorAction {
    return {
        type: SAVE_CAMPAIGN_CHAT_LIST_EDITOR,
        campaignId
    };
}

export const ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR =
    'ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR';
export type ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR = typeof ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR;

export interface OnDoneCampaignAllowedPageIdsEditorAction {
    type: ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR;
    campaignId: string;
    value: string[];
}

export function onDoneCampaignAllowedPageIdsEditor(
    campaignId: string,
    value: string[]
): OnDoneCampaignAllowedPageIdsEditorAction {
    return {
        type: ON_DONE_CAMPAIGN_ALLOWED_PAGE_IDS_EDITOR,
        campaignId,
        value
    };
}

export const UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE =
    'UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE';
export type UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE = typeof UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE;

export interface UpdateCampaignAllowedPageIdsValueAction {
    type: UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE;
    campaignId: string;
    value: string[];
}

export function updateCampaignAllowedPageIdsValue(
    campaignId: string,
    value: string[]
): UpdateCampaignAllowedPageIdsValueAction {
    return {
        type: UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE,
        campaignId,
        value
    };
}

export const ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR =
    'ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR';
export type ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR = typeof ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR;

export interface OnDoneCampaignBannedDomainsEditorAction {
    type: ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR;
    campaignId: string;
    value: string[];
}

export function onDoneCampaignBannedDomainsEditor(
    campaignId: string,
    value: string[]
): OnDoneCampaignBannedDomainsEditorAction {
    return {
        type: ON_DONE_CAMPAIGN_BANNED_DOMAINS_EDITOR,
        campaignId,
        value
    };
}

export const UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE =
    'UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE';
export type UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE = typeof UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE;

export interface UpdateCampaignBannedDomainsValueAction {
    type: UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE;
    campaignId: string;
    value: string[];
}

export function updateCampaignBannedDomainsValue(
    campaignId: string,
    value: string[]
): UpdateCampaignBannedDomainsValueAction {
    return {
        type: UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE,
        campaignId,
        value
    };
}

export const UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE =
    'UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE';
export type UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE = typeof UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE;

export interface UpdateCampaignNotifications {
    type: UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE;
    campaignId: string;
    notificationsValue: NotificationsValue;
}

export function updateCampaignNotifications(
    campaignId: string,
    notificationsValue: NotificationsValue
): UpdateCampaignNotifications {
    return {
        type: UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE,
        campaignId,
        notificationsValue
    };
}

export const SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS =
    'SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS';
export type SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS = typeof SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS;

export interface SaveNotificationsEditorForEditCampaigns {
    type: SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
}

export function saveNotificationsEditorForEditCampaigns(
    campaignId: string
): SaveNotificationsEditorForEditCampaigns {
    return {
        type: SAVE_NOTIFICATIONS_EDITOR_FOR_EDIT_CAMPAIGNS,
        campaignId
    };
}

export const ADD_END_DATE_FOR_EDIT_CAMPAIGNS =
    'ADD_END_DATE_FOR_EDIT_CAMPAIGNS';
export type ADD_END_DATE_FOR_EDIT_CAMPAIGNS = typeof ADD_END_DATE_FOR_EDIT_CAMPAIGNS;

export interface AddEndDateForEditCampaigns {
    type: ADD_END_DATE_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
}

export function addEndDateForEditCampaigns(
    campaignId: string
): AddEndDateForEditCampaigns {
    return {
        type: ADD_END_DATE_FOR_EDIT_CAMPAIGNS,
        campaignId
    };
}

export const DELETE_END_DATE_FOR_EDIT_CAMPAIGNS =
    'DELETE_END_DATE_FOR_EDIT_CAMPAIGNS';
export type DELETE_END_DATE_FOR_EDIT_CAMPAIGNS = typeof DELETE_END_DATE_FOR_EDIT_CAMPAIGNS;

export interface DeleteEndDateForEditCampaigns {
    type: DELETE_END_DATE_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
}

export function deleteEndDateForEditCampaigns(
    campaignId: string
): DeleteEndDateForEditCampaigns {
    return {
        type: DELETE_END_DATE_FOR_EDIT_CAMPAIGNS,
        campaignId
    };
}

export const ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR =
    'ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR';
export type ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR = typeof ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR;

export interface OnDoneCampaignRelevantPhrasesEditor {
    type: ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR;
    campaignId: string;
}

export function onDoneCampaignRelevantPhrasesEditor(
    campaignId: string
): OnDoneCampaignRelevantPhrasesEditor {
    return {
        type: ON_DONE_CAMPAIGN_RELEVANT_PHRASES_EDITOR,
        campaignId
    };
}

export const SAVE_CAMPAIGN_EXPERIMENTS_EDITOR =
    'SAVE_CAMPAIGN_EXPERIMENTS_EDITOR';
export type SAVE_CAMPAIGN_EXPERIMENTS_EDITOR = typeof SAVE_CAMPAIGN_EXPERIMENTS_EDITOR;

export interface SaveCampaignExperimentsEditorAction {
    type: SAVE_CAMPAIGN_EXPERIMENTS_EDITOR;
    campaignId: string;
}

export function saveCampaignExperimentsEditor(
    campaignId: string
): SaveCampaignExperimentsEditorAction {
    return {
        type: SAVE_CAMPAIGN_EXPERIMENTS_EDITOR,
        campaignId
    };
}

export const SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR =
    'SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR';
export type SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR = typeof SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR;

export interface SaveCampaignMetrikaKeyGoalsEditorAction {
    type: SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR;
    campaignId: string;
}

export function saveCampaignMetrikaKeyGoalsEditor(
    campaignId: string
): SaveCampaignMetrikaKeyGoalsEditorAction {
    return {
        type: SAVE_CAMPAIGN_METRIKA_KEY_GOALS_EDITOR,
        campaignId
    };
}

export const SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR =
    'SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR';
export type SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR = typeof SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR;

export interface SaveCampaignRateCorrectionsEditorAction {
    type: SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR;
    campaignId: string;
    editorId: string;
}

export function saveCampaignRateCorrectionsEditor(
    campaignId: string,
    editorId: string
): SaveCampaignRateCorrectionsEditorAction {
    return {
        type: SAVE_CAMPAIGN_RATE_CORRECTIONS_EDITOR,
        campaignId,
        editorId
    };
}

export const UPDATE_CAMPAIGN_RATE_CORRECTIONS =
    'UPDATE_CAMPAIGN_RATE_CORRECTIONS';
export type UPDATE_CAMPAIGN_RATE_CORRECTIONS = typeof UPDATE_CAMPAIGN_RATE_CORRECTIONS;

export interface UpdateCampaignRateCorrectionsAction {
    type: UPDATE_CAMPAIGN_RATE_CORRECTIONS;
    campaignId: string;
    rateCorrections: Record<RateCorrectionType, RateCorrection>;
}

export function updateCampaignRateCorrections(
    groupId: string,
    rateCorrections: Record<RateCorrectionType, RateCorrection>
): UpdateCampaignRateCorrectionsAction {
    return {
        type: UPDATE_CAMPAIGN_RATE_CORRECTIONS,
        campaignId: groupId,
        rateCorrections: rateCorrections
    };
}

export const UPDATE_CAMPAIGN_EXPERIMENTS = 'UPDATE_CAMPAIGN_EXPERIMENTS';
export type UPDATE_CAMPAIGN_EXPERIMENTS = typeof UPDATE_CAMPAIGN_EXPERIMENTS;

export interface UpdateCampaignExperimentsAction {
    type: UPDATE_CAMPAIGN_EXPERIMENTS;
    campaignId: string;
    experimentsValue: ExperimentsSaveValue;
}

export function updateCampaignExperiments(
    campaignId: string,
    experimentsValue: ExperimentsSaveValue
): UpdateCampaignExperimentsAction {
    return {
        type: UPDATE_CAMPAIGN_EXPERIMENTS,
        campaignId,
        experimentsValue
    };
}

export const UPDATE_CAMPAIGN_METRIKA_KEY_GOALS =
    'UPDATE_CAMPAIGN_METRIKA_KEY_GOALS';
export type UPDATE_CAMPAIGN_METRIKA_KEY_GOALS = typeof UPDATE_CAMPAIGN_METRIKA_KEY_GOALS;

export interface UpdateCampaignMetrikaKeyGoals {
    type: UPDATE_CAMPAIGN_METRIKA_KEY_GOALS;
    campaignId: string;
    selectedGoals: MetrikaSelectedKeyGoal[];
}

export function updateCampaignMetrikaKeyGoals(
    campaignId: string,
    selectedGoals: MetrikaSelectedKeyGoal[]
): UpdateCampaignMetrikaKeyGoals {
    return {
        type: UPDATE_CAMPAIGN_METRIKA_KEY_GOALS,
        campaignId,
        selectedGoals
    };
}

export const UPDATE_CAMPAIGN_STRATEGY = 'UPDATE_CAMPAIGN_STRATEGY';
export type UPDATE_CAMPAIGN_STRATEGY = typeof UPDATE_CAMPAIGN_STRATEGY;

export interface UpdateCampaignStrategyAction {
    type: UPDATE_CAMPAIGN_STRATEGY;
    campaignId: CampaignId;
    strategy: Strategy;
}

export function updateCampaignStrategy(
    campaignId: CampaignId,
    strategy: Strategy
): UpdateCampaignStrategyAction {
    return {
        type: UPDATE_CAMPAIGN_STRATEGY,
        campaignId,
        strategy
    };
}

export const SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR =
    'SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR';
export type SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR = typeof SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR;

export interface SaveCampaignMinusPhrasesEditor {
    type: SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR;
    campaignId: string;
}

export function saveCampaignMinusPhrasesEditor(
    campaignId: string
): SaveCampaignMinusPhrasesEditor {
    return {
        type: SAVE_CAMPAIGN_MINUS_PHRASES_EDITOR,
        campaignId
    };
}

export const SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS =
    'SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS';
export type SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS = typeof SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS;

export interface SaveTimeTargetingEditorForEditCampaigns {
    type: SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
}

export function saveTimeTargetingEditorForEditCampaigns(
    campaignId: string
): SaveTimeTargetingEditorForEditCampaigns {
    return {
        type: SAVE_TIME_TARGETING_EDITOR_FOR_EDIT_CAMPAIGNS,
        campaignId
    };
}

export const UPDATE_CAMPAIGN_MINUS_PHRASES = 'UPDATE_CAMPAIGN_MINUS_PHRASES';
export type UPDATE_CAMPAIGN_MINUS_PHRASES = typeof UPDATE_CAMPAIGN_MINUS_PHRASES;

export interface UpdateCampaignMinusPhrases {
    type: UPDATE_CAMPAIGN_MINUS_PHRASES;
    campaignId: string;
    value: MinusPhrasesValue;
}

export function updateCampaignMinusPhrases(
    campaignId: string,
    value: MinusPhrasesValue
): UpdateCampaignMinusPhrases {
    return {
        type: UPDATE_CAMPAIGN_MINUS_PHRASES,
        campaignId,
        value
    };
}

export const UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS =
    'UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS';
export type UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS = typeof UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS;

export interface UpdateCampaignTimeTargetingForEditCampaigns {
    type: UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
    value: TimeTargetingValue;
}

export function updateCampaignTimeTargetingForEditCampaigns(
    campaignId: string,
    value: TimeTargetingValue
): UpdateCampaignTimeTargetingForEditCampaigns {
    return {
        type: UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS,
        campaignId,
        value
    };
}

export interface UpdateCampaignAvailableForKeyGoalsPayload {
    campaignId: string;
    goals: Record<string, Goal>;
}

export const UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS =
    'UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS';
export type UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS = typeof UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS;

export interface UpdateCampaignAvailableForKeyGoals {
    type: UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS;
    isMetrikaAvailable: boolean;
    campaignGoals: UpdateCampaignAvailableForKeyGoalsPayload[];
}

export function updateCampaignAvailableForKeyGoals(
    isMetrikaAvailable: boolean,
    campaignGoals: UpdateCampaignAvailableForKeyGoalsPayload[]
): UpdateCampaignAvailableForKeyGoals {
    return {
        type: UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS,
        isMetrikaAvailable,
        campaignGoals
    };
}

export const SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN =
    'SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN';
export type SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN = typeof SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN;

export interface SaveVisitingCardForEditCampaign {
    type: SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN;
    campaignId: string;
}

export function saveVisitingCardForEditCampaign(
    campaignId: string
): SaveVisitingCardForEditCampaign {
    return {
        type: SAVE_VISITING_CARD_FOR_EDIT_CAMPAIGN,
        campaignId
    };
}

export const SAVE_SUCCESS_VISITING_CARD_FOR_EDIT_CAMPAIGN =
    'SAVE_SUCCESS_VISITING_CARD_FOR_EDIT_CAMPAIGN';
export type SAVE_SUCCESS_VISITING_CARD_FOR_EDIT_CAMPAIGN = typeof SAVE_SUCCESS_VISITING_CARD_FOR_EDIT_CAMPAIGN;

export interface SaveSuccessVisitingCardForEditCampaign {
    type: SAVE_SUCCESS_VISITING_CARD_FOR_EDIT_CAMPAIGN;
    campaignIds: string[];
    vCardsIds: string[];
}

export function saveSuccessVisitingCardForEditCampaign(
    campaignIds: string[],
    vCardsIds: string[]
): SaveSuccessVisitingCardForEditCampaign {
    return {
        type: SAVE_SUCCESS_VISITING_CARD_FOR_EDIT_CAMPAIGN,
        campaignIds,
        vCardsIds
    };
}

export const UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS =
    'UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS';
export type UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS = typeof UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS;

export interface UpdateVisitingCardsForEditCampaigns {
    type: UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS;
    vCardsData: Record<string, VcardData>;
    metroStations: Record<number, Array<MetroStationInfo>>;
}

export const updateVisitingCardsForEditCampaigns = (
    vCardsData: Record<string, VcardData>,
    metroStations: Record<number, Array<MetroStationInfo>>
): UpdateVisitingCardsForEditCampaigns => {
    return {
        type: UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS,
        vCardsData,
        metroStations
    };
};

export const SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS =
    'SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS';
export type SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS = typeof SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS;

export interface SynchronizeVisitingCardForEditCampaigns {
    type: SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
}

export const synchronizeVisitingCardForEditCampaigns = (
    campaignId: string
): SynchronizeVisitingCardForEditCampaigns => {
    return {
        type: SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS,
        campaignId
    };
};

export const CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS =
    'CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS';
export type CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS = typeof CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS;

export interface ChangeNotificationsDisableValueForEditCampaigns {
    type: CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS;
    campaignId: string;
    index: number;
    disableValue: boolean;
}

export const changeNotificationsDisableValueForEditCampaigns = (
    campaignId: string,
    index: number,
    disableValue: boolean
): ChangeNotificationsDisableValueForEditCampaigns => {
    return {
        type: CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS,
        campaignId,
        index,
        disableValue
    };
};

export type CampaignsEditActions =
    | GetCampaignsForEditAction
    | UpdateDataForEditCampaignsAction
    | ShowCampaignEditGlobalErrorAction
    | UpdateCampaignChat
    | UpdateCampaignRelevantPhrases
    | UpdateCampaignMetrikaCounters
    | SaveCampaignMetrikaCounterEditorAction
    | ChangeBannersTextSubstitutionAction
    | SaveCampaignsAction
    | UpdateCampaignNameAction
    | ChangeCampaignsEditPageStatusAction
    | SaveCampaignNameEditorChangesAction
    | ChangeShowYandexCatalogInfoAction
    | ChangeConsiderCompetitorsAction
    | ChangeBannersLanguage
    | SaveCampaignChatListEditorAction
    | UpdateCampaignDisabledIpsValueAction
    | OnDoneCampaignDisabledIpsEditor
    | ToggleCampaignOtherSettingsVisibilityAction
    | ToggleCampaignsOtherSettingsVisibilityAction
    | OnDoneCampaignAllowedPageIdsEditorAction
    | UpdateCampaignAllowedPageIdsValueAction
    | OnDoneCampaignBannedDomainsEditorAction
    | UpdateCampaignBannedDomainsValueAction
    | UpdateCampaignNotifications
    | SaveNotificationsEditorForEditCampaigns
    | SaveCampaignExperimentsEditorAction
    | UpdateCampaignExperimentsAction
    | SaveCampaignMetrikaKeyGoalsEditorAction
    | UpdateCampaignMetrikaKeyGoals
    | ShowCampaignEditGeneralErrorsAction
    | LeaveCampaignsEditAction
    | OnDoneCampaignRelevantPhrasesEditor
    | GetRetargetingConditionsForCampaignsEditAction
    | UpdateRetargetingConditionsForCampaignsEditAction
    | SaveCampaignRateCorrectionsEditorAction
    | UpdateCampaignRateCorrectionsAction
    | ChangeMarkTargetCallLinksFlagAction
    | AddEndDateForEditCampaigns
    | DeleteEndDateForEditCampaigns
    | ChangeSiteMonitoringFlagForEditCampaigns
    | SaveCampaignMinusPhrasesEditor
    | UpdateCampaignMinusPhrases
    | SaveTimeTargetingEditorForEditCampaigns
    | UpdateCampaignTimeTargetingForEditCampaigns
    | ShowCampaignEditItemErrorsAction
    | UpdateCampaignStrategyAction
    | FetchCampaignsGoalsAvailableForKeyGoals
    | UpdateCampaignAvailableForKeyGoals
    | SaveVisitingCardForEditCampaign
    | SaveSuccessVisitingCardForEditCampaign
    | UpdateVisitingCardsForEditCampaigns
    | SynchronizeVisitingCardForEditCampaigns
    | ChangeNotificationsDisableValueForEditCampaigns
    | ChangeHasExtendedGeoTargetingAction;
